<script setup lang="ts">
import type { Component } from 'vue'

/**
 * Comfortable menu row for the account / create menus (and any ≥44px touch menu): leading icon +
 * label + optional trailing content (count, chevron). Distinct from BaseMenuItem, which is the
 * small dropdown row (text-xs, trailing check). Single-root <button> so role="menuitem" / @click /
 * disabled fall through.
 *
 * `size`: dense (desktop dropdown, text-sm) · comfortable (mobile sheet, text-[15px], taller).
 */
interface Props {
  icon?: Component
  /** Extra classes for the icon (e.g. a tint like the red Record dot). */
  iconClass?: string
  size?: 'dense' | 'comfortable'
  disabled?: boolean
}
const props = withDefaults(defineProps<Props>(), { size: 'dense', disabled: false })
</script>

<template>
  <button
    type="button"
    :disabled="props.disabled"
    class="flex w-full items-center gap-3 text-left text-gray-700 transition-colors hover:cursor-pointer hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
    :class="props.size === 'comfortable' ? 'px-4 py-3 text-[15px]' : 'px-4 py-2 text-sm'"
  >
    <component
      :is="props.icon"
      v-if="props.icon"
      class="shrink-0"
      :class="[props.size === 'comfortable' ? 'h-5 w-5' : 'h-4 w-4', props.iconClass]"
    />
    <slot />
    <span v-if="$slots.trailing" class="ml-auto"><slot name="trailing" /></span>
  </button>
</template>
